import { EChartsOption } from 'echarts';
import {
  ChartDataPoint,
  ChartDataSeries,
  dataViewFormatter,
  getTimestampedFileNameForWidget,
  RowData,
} from '../helpers/widgets';

interface AbundanceList {
  name: string;
  ordinalValue: number;
}

interface TaongaData {
  [name: string]: {
    medianOrdinal: number;
    medianName: string;
    icon: string;
  };
}

interface TaongaReportingData {
  abundance: AbundanceList[];
  taongaMedians: TaongaData;
}

const colour = [
  '#f26b4b', // Orange
  '#0582bf', // Dark blue
  '#8c7ebb', // Light purple
  '#00a7ca', // Mid teal
  '#473b7b', // Dark purple
  '#66c39b', // Mid green
  '#82afdd', // Mid cornflower
  '#c83c7b', // Dark magenta
  '#86cdae', // Light green
  '#007093', // Dark teal
  '#a8ceee', // Light cornflower
  '#26312b', // Charcoal
  '#00916f', // Dark green
  '#615097', // Mid purple
  '#00b1d8', // Light blue
  '#5fc9de', // Light teal
  '#ee4891', // Mid magenta
  '#6894cd', // Dark cornflower
  '#0395ca', // Mid blue
];

const taongaTypes = [
  'fish',
  'plant',
  'tree',
  'crustacean',
  'shellfish',
  'seafish',
  'bird',
  'insect',
  'octopus-other-sealife',
  'lizard',
  'seal-other-mammal',
  'amphibian',
  'seaweed-ocean-or-freshwater',
  'snail',
  'whale-large-mammal',
];

/**
 * Renders a cell in the data table.
 *
 * This is a custom renderer to handle both text and image icons, overriding the default behavior.
 *
 * @param series Taonga data series
 * @param dataPoint The data point for the cell
 * @returns The HTML string for the cell
 */
const renderCell = (series: ChartDataSeries, dataPoint?: RowData) => {
  const isObject = typeof dataPoint === 'object';
  const symbol = isObject ? dataPoint.symbol : dataPoint;
  if (series.name === 'icon') {
    const url = typeof symbol === 'string' && symbol.startsWith('image://') ? symbol.slice('image://'.length) : '';
    return url ? `<img src="${url}" width="25" height="25" alt="Data Icon">` : '-';
  }

  return isObject ? String(dataPoint.value) : String(dataPoint);
};

const getTaongaSymbol = (name: string): string | null => {
  if (!taongaTypes.includes(name)) return null;
  return `image://assets/icons/taonga/${name}.svg`;
};

const getTaongaChartConfig = (abundance: AbundanceList[], taonga: TaongaData): EChartsOption => {
  const smartSplitLabel = (label: string) =>
    /[\/|()]/.test(label)
      ? label
          .replace(/\s+/g, '')
          .replace(/\(.*?\)/g, '')
          .split(/[\/|]/)
          .join('\n')
      : label;
  const formattedData = Object.keys(taonga).map((name, index) => {
    const details = taonga[name];
    return {
      name,
      value: details.medianOrdinal,
      medianName: details.medianName,
      itemStyle: {
        color: colour[index % colour.length], // pick from the color array
      },
      icon: getTaongaSymbol(details.icon) ?? null,
    };
  });

  return {
    tooltip: {
      formatter: function (info: any) {
        const { name, value, data } = info;
        if (data.medianName) return `<strong>${data.medianName}</strong>`;
        return `<strong>${name}</strong>`;
      },
    },
    toolbox: {
      show: true,
      feature: {
        mark: { show: true },
        dataView: {
          show: true,
          readOnly: false,
          optionToContent: (option: any): string | HTMLElement =>
            dataViewFormatter(option as EChartsOption, renderCell),
        },
        saveAsImage: {
          show: true,
          name: getTimestampedFileNameForWidget('rongo-taonga-abundance'),
        },
      },
    },
    xAxis: {
      data: formattedData.map((item) => smartSplitLabel(item.name)),
      axisTick: { show: false },
      axisLine: { show: false },
      axisLabel: {
        lineHeight: 16,
        interval: 0,
      },
    },
    yAxis: {
      type: 'category',
      offset: 40,
      data: abundance.sort((a, b) => a.ordinalValue - b.ordinalValue).map((item) => item.name),
      axisLabel: {
        show: true,
        formatter: function (value: string) {
          return value
            .split('|')
            .map((s) => s.trim())
            .join('\n');
        },
        align: 'right',
        verticalAlign: 'middle',
        lineHeight: 16,
        margin: -35,
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: ['#b0b0b0'], // Slightly darker gray
          width: 1,
          type: 'dashed',
        },
      },
      axisTick: { show: false },
      axisLine: { show: false },
    },
    grid: {
      top: '10%',
      right: 0,
      left: '13%',
    },
    series: [
      {
        name: 'count',
        type: 'pictorialBar',
        barCategoryGap: '-30%',
        symbol: 'path://M0,10 L10,10 C5.5,10 5.5,5 5,0 C4.5,5 4.5,10 0,10 z',
        itemStyle: {
          opacity: 0.5,
        },
        emphasis: {
          itemStyle: {
            opacity: 1,
          },
        },
        data: formattedData,
        z: 10,
      },
      {
        name: 'icon',
        type: 'pictorialBar',
        barGap: '-100%',
        symbolPosition: 'end',
        symbolSize: [40, 30],
        symbolOffset: [0, '-120%'],
        data: formattedData.map((item) => ({
          value: item.value,
          symbol: item.icon || undefined,
          symbolSize: item.icon ? 30 : 0,
        })),
      },
    ],
  };
};

export { getTaongaChartConfig, TaongaReportingData, TaongaData, AbundanceList };
